import React, { useRef } from 'react';
import {
  ChevronLeft, Type, Image as ImageIcon, Shapes, Undo2, Redo2,
  Save, ArrowRight, Sparkles, Square, Circle, Triangle, Minus,
  ScanEye, Barcode,
} from 'lucide-react';
import { CanvasElement, TextElement, ImageElement, RectElement, CircleElement, LineElement, TriangleElement } from '../types';
import './DesignerToolbar.css';

let idCounter = 0;
export function genId() { return `el_${Date.now()}_${idCounter++}`; }

interface DesignerToolbarProps {
  background: string;
  stageWidth: number;
  stageHeight: number;
  isSaving: boolean;
  canUndo: boolean;
  canRedo: boolean;
  showAIPanel: boolean;
  onAddElement: (el: CanvasElement) => void;
  onSetBackground: (color: string) => void;
  onUndo: () => void;
  onRedo: () => void;
  onSave: () => void;
  onSaveAndExit: () => void;
  onBack: () => void;
  onShowTemplates: () => void;
  onToggleAIPanel: () => void;
  onShowPreview: () => void;
  onAddISBN: () => void;
  onSetBackgroundLive: (color: string) => void;
}


/* ── Component ── */
const DesignerToolbar: React.FC<DesignerToolbarProps> = ({
  background, stageWidth, stageHeight, isSaving, canUndo, canRedo, showAIPanel,
  onAddElement, onSetBackground, onUndo, onRedo, onSave, onSaveAndExit,
  onBack, onShowTemplates, onToggleAIPanel, onShowPreview, onAddISBN, onSetBackgroundLive,
}) => {
  const imageInputRef = useRef<HTMLInputElement>(null);
  const bgInputRef    = useRef<HTMLInputElement>(null);
  const [showShapeMenu, setShowShapeMenu] = React.useState(false);

  const cx = stageWidth / 2;
  const cy = stageHeight / 2;

  const addText = () => {
    const el: TextElement = {
      id: genId(), type: 'text', name: 'Text',
      x: cx - 100, y: cy - 20, width: 200, height: 40,
      rotation: 0, opacity: 1, visible: true,
      text: 'Double-click to edit',
      fontSize: 24, fontFamily: 'Fraunces',
      fontStyle: 'normal', fill: '#111827', align: 'center',
      letterSpacing: 0, lineHeight: 1.2, textTransform: 'none',
      strokeColor: '', strokeWidth: 0, strokePosition: 'outer' as const,
      shadowEnabled: false, shadowColor: '#000000',
      shadowBlur: 10, shadowOffsetX: 0, shadowOffsetY: 5,
    };
    onAddElement(el);
  };

  const addRect = () => {
    const el: RectElement = {
      id: genId(), type: 'rect', name: 'Rectangle',
      x: cx - 60, y: cy - 40, width: 120, height: 80,
      rotation: 0, opacity: 1, visible: true,
      fill: '#f97316', stroke: '', strokeWidth: 0, cornerRadius: 0,
    };
    onAddElement(el);
    setShowShapeMenu(false);
  };

  const addCircle = () => {
    const el: CircleElement = {
      id: genId(), type: 'circle', name: 'Circle',
      x: cx - 50, y: cy - 50, width: 100, height: 100,
      rotation: 0, opacity: 1, visible: true,
      fill: '#111827', stroke: '', strokeWidth: 0,
    };
    onAddElement(el);
    setShowShapeMenu(false);
  };

  const addTriangle = () => {
    const el: TriangleElement = {
      id: genId(), type: 'triangle', name: 'Triangle',
      x: cx - 50, y: cy - 55, width: 100, height: 110,
      rotation: 0, opacity: 1, visible: true,
      fill: '#111827', stroke: '', strokeWidth: 0,
    };
    onAddElement(el);
    setShowShapeMenu(false);
  };

  const addLine = () => {
    const el: LineElement = {
      id: genId(), type: 'line', name: 'Line',
      x: cx - 80, y: cy, width: 160, height: 0,
      rotation: 0, opacity: 1, visible: true,
      stroke: '#111827', strokeWidth: 3, cap: 'round',
    };
    onAddElement(el);
    setShowShapeMenu(false);
  };

  const handleImageFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const src = ev.target?.result as string;
      const img = new Image();
      img.onload = () => {
        const aspect = img.width / img.height;
        const h = Math.min(stageHeight * 0.6, img.height);
        const w = h * aspect;
        const el: ImageElement = {
          id: genId(), type: 'image', name: file.name,
          x: cx - w / 2, y: cy - h / 2, width: w, height: h,
          rotation: 0, opacity: 1, visible: true,
          src, flipX: false, flipY: false,
        };
        onAddElement(el);
      };
      img.src = src;
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  React.useEffect(() => {
    if (!showShapeMenu) return;
    const handler = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest('.dtb-dropdown-wrap')) {
        setShowShapeMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showShapeMenu]);

  return (
    <div className="dtb-root">

      {/* ── Back (icon only) ── */}
      <button className="dtb-btn-back" onClick={onBack} title="Back to book">
        <ChevronLeft size={16} strokeWidth={1.75} />
      </button>

      <div className="dtb-divider" />

      {/* ── Tool cluster ── */}
      <div className="dtb-tool-cluster">

        <button className="dtb-tool-btn" onClick={addText} title="Add text">
          <Type size={14} strokeWidth={1.75} /> Text
        </button>

        <button className="dtb-tool-btn" onClick={() => imageInputRef.current?.click()} title="Upload image">
          <ImageIcon size={14} strokeWidth={1.75} /> Image
        </button>
        <input ref={imageInputRef} type="file" accept="image/*" onChange={handleImageFile} style={{ display: 'none' }} />

        <div className="dtb-dropdown-wrap">
          <button className="dtb-tool-btn"
            onClick={() => setShowShapeMenu(v => !v)}
            title="Add shape">
            <Shapes size={14} strokeWidth={1.75} /> Shape <span className="dtb-caret">▾</span>
          </button>
          {showShapeMenu && (
            <div className="dtb-dropdown">
              <button className="dtb-dropdown-item" onClick={addRect}><Square size={13} strokeWidth={1.5} /> Rectangle</button>
              <button className="dtb-dropdown-item" onClick={addCircle}><Circle size={13} strokeWidth={1.5} /> Ellipse</button>
              <button className="dtb-dropdown-item" onClick={addTriangle}><Triangle size={13} strokeWidth={1.5} /> Triangle</button>
              <button className="dtb-dropdown-item" onClick={addLine}><Minus size={13} strokeWidth={2} /> Line</button>
            </div>
          )}
        </div>

        <button className="dtb-tool-btn" onClick={onAddISBN} title="Add ISBN barcode placeholder to back cover">
          <Barcode size={14} strokeWidth={1.75} /> ISBN
        </button>

        <button className="dtb-bg-wrap" onClick={() => bgInputRef.current?.click()} title="Canvas background">
          <span className="dtb-bg-label">BG</span>
          <div className="dtb-bg-swatch-wrap">
            <div className="dtb-bg-swatch-display" style={{ background }} />
            <input ref={bgInputRef} type="color" value={background}
              onChange={e => onSetBackgroundLive(e.target.value)}
              onBlur={e => onSetBackground(e.target.value)}
              className="dtb-bg-swatch-input" />
          </div>
        </button>

      </div>

      <div className="dtb-divider" />

      {/* ── Templates ── */}
      <button className="dtb-btn-templates" onClick={onShowTemplates}>
        Templates
      </button>

      {/* ── AI Cover ── */}
      <button className={`dtb-btn-ai${showAIPanel ? ' active' : ''}`} onClick={onToggleAIPanel}>
        <Sparkles size={13} strokeWidth={1.75} /> Cover generation
      </button>

      <div className="dtb-divider" />

      {/* ── History ── */}
      <button className="dtb-btn-history" onClick={onUndo} disabled={!canUndo} title="Undo (Ctrl+Z)">
        <Undo2 size={15} strokeWidth={1.75} />
      </button>
      <button className="dtb-btn-history" onClick={onRedo} disabled={!canRedo} title="Redo (Ctrl+Y)">
        <Redo2 size={15} strokeWidth={1.75} />
      </button>

      <div className="dtb-spacer" />

      {/* ── Preview ── */}
      <button className="dtb-btn-preview" onClick={onShowPreview} title="Preview cover">
        <ScanEye size={14} strokeWidth={1.75} /> Preview
      </button>

      {/* ── Save ── */}
      <button className="dtb-btn-save" onClick={onSave} disabled={isSaving} title="Save and stay in designer">
        <Save size={14} strokeWidth={1.75} />
        {isSaving ? 'Saving…' : 'Save'}
      </button>

      {/* ── Use Cover ── */}
      <button className="dtb-btn-use" onClick={onSaveAndExit} disabled={isSaving} title="Save and use this cover">
        {isSaving ? 'Saving…' : 'Use Cover'}
        <ArrowRight size={13} strokeWidth={2} />
      </button>

    </div>
  );
};

export default DesignerToolbar;
